import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * Select customizado, acessível e com busca.
 *
 * Melhorias sobre a versão anterior:
 * - Navegação por teclado (setas, Home/End, Enter, Escape) — antes só funcionava
 *   com o mouse, o que também deixava o campo inutilizável por leitor de tela.
 * - Semântica de combobox/listbox, para o leitor anunciar o estado.
 * - Busca por digitação quando há muitas opções: listas de safras e insumos
 *   crescem e rolar até o item ficava lento.
 * - Reposiciona ao rolar a página e abre para cima quando não há espaço embaixo.
 *
 * Sobre `required`: ela só marca o campo como obrigatório para leitores de tela
 * (`aria-required`). Antes existia aqui um `<input required>` espelho de 0×0 px:
 * como o navegador não consegue focar um elemento de tamanho zero para mostrar a
 * bolha de validação, ele abortava o submit em silêncio e o formulário parecia
 * travado. Quem usa Select obrigatório valida em JS no próprio handler de submit.
 */
export default function Select({
  value, onChange, options = [], placeholder = 'Selecione',
  required, disabled, style, buscavel,
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, acima: false });
  const [ativo, setAtivo] = useState(-1);
  const [busca, setBusca] = useState('');
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const buscaRef = useRef(null);

  // Busca aparece sozinha em listas longas: abaixo disso ela só ocuparia espaço.
  const mostrarBusca = buscavel ?? options.length > 8;

  const filtradas = useMemo(() => {
    if (!mostrarBusca || !busca.trim()) return options;
    const t = busca.trim().toLowerCase();
    return options.filter(o => String(o.label).toLowerCase().includes(t));
  }, [options, busca, mostrarBusca]);

  const selected = options.find(o => String(o.value) === String(value));

  const recalcular = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const alturaLista = Math.min(260, filtradas.length * 44 + (mostrarBusca ? 52 : 0) + 12);
    const espacoAbaixo = window.innerHeight - r.bottom;
    const acima = espacoAbaixo < alturaLista && r.top > espacoAbaixo;
    setPos({
      top: acima ? r.top + window.scrollY - alturaLista - 4 : r.bottom + window.scrollY + 4,
      left: r.left + window.scrollX,
      width: r.width,
      acima,
    });
  }, [filtradas.length, mostrarBusca]);

  useEffect(() => {
    const fora = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        listRef.current && !listRef.current.contains(e.target)
      ) fechar();
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('touchstart', fora);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('touchstart', fora);
    };
  }, []);

  // A lista é renderizada em portal com posição absoluta: sem isso ela "descola"
  // do campo quando a página rola ou a janela muda de tamanho.
  useEffect(() => {
    if (!open) return;
    recalcular();
    window.addEventListener('scroll', recalcular, true);
    window.addEventListener('resize', recalcular);
    return () => {
      window.removeEventListener('scroll', recalcular, true);
      window.removeEventListener('resize', recalcular);
    };
  }, [open, recalcular]);

  useEffect(() => {
    if (open && mostrarBusca) buscaRef.current?.focus();
  }, [open, mostrarBusca]);

  const fechar = () => { setOpen(false); setBusca(''); setAtivo(-1); };

  const abrir = () => {
    if (disabled) return;
    recalcular();
    setOpen(true);
    const idx = filtradas.findIndex(o => String(o.value) === String(value));
    setAtivo(idx);
  };

  const escolher = (v) => { onChange(String(v)); fechar(); triggerRef.current?.querySelector('button')?.focus(); };

  const onKeyDown = (e) => {
    if (disabled) return;

    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        abrir();
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        fechar();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setAtivo(i => Math.min(i + 1, filtradas.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setAtivo(i => Math.max(i - 1, 0));
        break;
      case 'Home':
        e.preventDefault();
        setAtivo(0);
        break;
      case 'End':
        e.preventDefault();
        setAtivo(filtradas.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (ativo >= 0 && filtradas[ativo]) escolher(filtradas[ativo].value);
        break;
      case 'Tab':
        fechar();
        break;
      default:
        break;
    }
  };

  // Mantém o item destacado visível ao navegar com as setas.
  useEffect(() => {
    if (!open || ativo < 0) return;
    const el = listRef.current?.querySelector(`[data-idx="${ativo}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [ativo, open]);

  return (
    <div className="custom-select" ref={triggerRef} onKeyDown={onKeyDown}>
      <button
        type="button"
        className="custom-select-trigger"
        onClick={() => (open ? fechar() : abrir())}
        style={{ ...style, ...(disabled ? { opacity: 0.55, cursor: 'not-allowed' } : {}) }}
        disabled={disabled}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-required={required || undefined}
      >
        <span className={selected ? 'custom-select-value' : 'custom-select-placeholder'}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="custom-select-arrow" aria-hidden="true">{open ? '▲' : '▼'}</span>
      </button>

      {open && createPortal(
        <div
          ref={listRef}
          className="custom-select-pop"
          style={{ position: 'absolute', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
        >
          {mostrarBusca && (
            <input
              ref={buscaRef}
              className="custom-select-busca"
              value={busca}
              onChange={e => { setBusca(e.target.value); setAtivo(0); }}
              placeholder="Buscar..."
              aria-label="Buscar opção"
            />
          )}
          <ul className="custom-select-options" role="listbox">
            {placeholder && !busca && (
              <li
                role="option"
                aria-selected={!value}
                className={`custom-select-option ${!value ? 'active' : ''}`}
                onClick={() => { onChange(''); fechar(); }}
              >
                {placeholder}
              </li>
            )}
            {filtradas.map((o, i) => (
              <li
                key={o.value}
                data-idx={i}
                role="option"
                aria-selected={String(o.value) === String(value)}
                className={`custom-select-option ${String(o.value) === String(value) ? 'active' : ''} ${i === ativo ? 'destacado' : ''}`}
                onClick={() => escolher(o.value)}
                onMouseEnter={() => setAtivo(i)}
              >
                {o.label}
              </li>
            ))}
            {filtradas.length === 0 && (
              <li className="custom-select-option" style={{ color: 'var(--text-muted)', cursor: 'default' }}>
                Nenhuma opção encontrada
              </li>
            )}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
}
